import React, {useState} from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import FolderIcon from '@mui/icons-material/Folder';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BugReportIcon from '@mui/icons-material/BugReport';
import SettingsIcon from '@mui/icons-material/Settings';
import logo from '../images/logo.png';
import {useAuth} from '../contexts/AuthContext';
import {useNavigate, useLocation} from 'react-router-dom';
import GravatarAvatar from './GravatarAvatar';
import {NotificationBell} from './NotificationBell';

export default function ShellNavigation() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {user, signOut} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuClose = () => setAnchorEl(null);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
    handleMenuClose();
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const isActive = (prefix: string) =>
    location.pathname === prefix || location.pathname.startsWith(prefix + '/');

  return (
    <Box
      sx={{
        height: 56,
        borderBottom: '1px solid #E5E7EB',
        px: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        bgcolor: '#fff',
        flexShrink: 0,
      }}
    >
      {/* Left: Logo & Nav Items */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Box
          sx={{display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer'}}
          onClick={() => navigate(user ? '/app' : '/explore')}
        >
          <img src={logo} alt="Planapus Logo" style={{height: 32, width: 'auto'}} />
          <Typography sx={{fontWeight: 600, fontSize: 18}}>Planapus</Typography>
        </Box>

        {/* Guides — always visible */}
        <Button
          startIcon={<ExploreIcon />}
          onClick={() => navigate('/explore')}
          color={isActive('/explore') ? 'primary' : 'inherit'}
          sx={{fontWeight: isActive('/explore') ? 600 : 400, ml: 1}}
        >
          Guides
        </Button>

        {user && (
          <>
            {/* My Work */}
            <Button
              startIcon={<FolderIcon />}
              onClick={() => navigate('/app/plans')}
              color={isActive('/app/plans') ? 'primary' : 'inherit'}
              sx={{fontWeight: isActive('/app/plans') ? 600 : 400}}
            >
              My Work
            </Button>

            {/* Admin */}
            <Button
              startIcon={<ListAltIcon />}
              onClick={() => navigate('/org/guides')}
              color={isActive('/org') ? 'primary' : 'inherit'}
              sx={{fontWeight: isActive('/org') ? 600 : 400}}
            >
              Admin
            </Button>
          </>
        )}
      </Box>

      {/* Right: Auth */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        {user ? (
          <>
            <NotificationBell />
            <IconButton onClick={e => setAnchorEl(e.currentTarget)} size="small">
              <GravatarAvatar
                email={user.email}
                name={user.displayName || user.email || '?'}
                size={32}
                showTooltip={false}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
              transformOrigin={{vertical: 'top', horizontal: 'right'}}
            >
              <Box sx={{px: 2, py: 1}}>
                <Typography variant="body2" color="text.secondary">
                  {user.phoneNumber || user.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => handleNavigate('/app/settings')}
                selected={isActive('/app/settings')}
              >
                <SettingsIcon sx={{mr: 1, fontSize: 20}} />
                Settings
              </MenuItem>
              <MenuItem
                onClick={() => handleNavigate('/app/debug')}
                selected={isActive('/app/debug')}
              >
                <BugReportIcon sx={{mr: 1, fontSize: 20}} />
                Debug
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={() => navigate('/auth')}
            startIcon={<AccountCircleIcon />}
          >
            Sign In
          </Button>
        )}
      </Box>
    </Box>
  );
}
